import { Icon } from '../../../../components/icons/Icon'
import { InlineAlert } from '../../../../components/InlineAlert'
import { HelpBalloon } from '../../../../help/HelpBalloon'
import { FilterChipBar } from '../../filter/FilterChipBar'
import { WIDGET_KEY } from '../../widgets/types'
import { WidgetErrorBoundary } from '../../widgets/WidgetErrorBoundary'
import { CaseAgingWidget } from './CaseAgingWidget'
import { IngestionRunVolumeWidget } from './IngestionRunVolumeWidget'
import { AiAcceptRateWidget } from './AiAcceptRateWidget'
import { DashboardThemePicker } from '../../../../theme/DashboardThemePicker'
import type { UseOperationsAnalyticsResult } from './useOperationsAnalytics'
import type { DashboardFilter } from '../../../../types/insights'
import '../../charts/charts.css'

const AXIS_LABELS: Record<string, string> = {
  fiscalYearId: 'Fiscal year',
  accountId: 'Account',
  legalEntityId: 'Legal entity',
  kind: 'Kind',
  programId: 'Program',
  currentState: 'Status',
}

/**
 * IB-282 View 4 — `/insights/dashboards/operations`. Presentational grid of `WidgetFrame`-wrapped
 * tiles, each in its own `WidgetErrorBoundary` (double isolation, mirrors every sibling view's exact
 * composition). Pure props-in, `useOperationsAnalytics` owns all state.
 *
 * <p>Sprint-1 scope is 4.1 (case aging) + 4.2 (ingestion/AI-accept volumes) ONLY — the RAID
 * I-45-cut metric and the deferred per-team capacity concept named in the parent DoR are
 * deliberately absent, mechanically asserted by `scopeGuard.test.ts`.
 */
export function OperationsAnalyticsPanel(props: UseOperationsAnalyticsResult) {
  const {
    state,
    dispatch,
    unknownKeys,
    noticeDismissed,
    dismissNotice,
    filterFieldsOpen,
    setFilterFieldsOpen,
    widgetProps,
    ingestionRunVolumeAvailable,
    aiAcceptRateAvailable,
    refreshKey,
  } = props

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--iw-space-3)',
          flexWrap: 'wrap',
          padding: 'var(--iw-space-4) var(--iw-space-5)',
          borderBottom: '1px solid var(--iw-border)',
          background: 'var(--iw-surface)',
        }}
      >
        <span style={{ color: 'var(--iw-accent)', display: 'inline-flex' }}>
          <Icon name="activity" size={20} />
        </span>
        <h1 style={{ fontSize: 'var(--iw-size-lg)', margin: 0 }}>Operational / throughput analytics</h1>
        <HelpBalloon id="list.insights" />
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="iw-btn iw-btn--sm"
          aria-expanded={filterFieldsOpen}
          onClick={() => setFilterFieldsOpen(!filterFieldsOpen)}
        >
          <Icon name="filter" size={14} />
          Filters
        </button>
        <DashboardThemePicker />
      </div>

      {filterFieldsOpen ? <OperationsFilterFields state={state} dispatch={dispatch} /> : null}

      {unknownKeys.length > 0 && !noticeDismissed ? (
        <div style={{ padding: '0 var(--iw-space-5)', paddingTop: 'var(--iw-space-3)' }}>
          <InlineAlert
            severity="warning"
            action={
              <button type="button" className="iw-btn iw-btn--sm" onClick={dismissNotice}>
                Dismiss
              </button>
            }
          >
            Some filters in this link could not be applied and were ignored.
          </InlineAlert>
        </div>
      ) : null}

      <FilterChipBar state={state} dispatch={dispatch} axisLabels={AXIS_LABELS} />

      <div className="iw-dashboard-grid">
        <WidgetErrorBoundary title="Case aging by current status" icon="clock">
          <CaseAgingWidget {...widgetProps(WIDGET_KEY.caseAgingByStatus)} />
        </WidgetErrorBoundary>
        <WidgetErrorBoundary title="Ingestion runs/day" icon="upload">
          <IngestionRunVolumeWidget available={ingestionRunVolumeAvailable} refreshKey={refreshKey} />
        </WidgetErrorBoundary>
        <WidgetErrorBoundary title="AI-accept rate" icon="activity">
          <AiAcceptRateWidget available={aiAcceptRateAvailable} refreshKey={refreshKey} />
        </WidgetErrorBoundary>
      </div>
    </div>
  )
}

interface OperationsFilterFieldsProps {
  state: UseOperationsAnalyticsResult['state']
  dispatch: UseOperationsAnalyticsResult['dispatch']
}

/** Freeform refinement inputs — same disclosed "raw-ID input, no lookup picker yet" posture as every sibling view. */
function OperationsFilterFields({ state, dispatch }: OperationsFilterFieldsProps) {
  const fields: Array<{ axis: keyof typeof AXIS_LABELS; placeholder: string }> = [
    { axis: 'fiscalYearId', placeholder: 'Fiscal year ID' },
    { axis: 'accountId', placeholder: 'Account ID' },
    { axis: 'legalEntityId', placeholder: 'Legal entity ID' },
    { axis: 'programId', placeholder: 'Program ID' },
  ]
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--iw-space-3)',
        padding: 'var(--iw-space-3) var(--iw-space-5)',
        borderBottom: '1px solid var(--iw-border)',
      }}
    >
      {fields.map((f) => (
        <div className="iw-field" key={f.axis}>
          <label className="iw-field-label" htmlFor={`operations-analytics-filter-${f.axis}`}>
            {AXIS_LABELS[f.axis]}
          </label>
          <input
            id={`operations-analytics-filter-${f.axis}`}
            className="iw-input"
            defaultValue={state.filters[f.axis as keyof DashboardFilter] ?? ''}
            placeholder={f.placeholder}
            onBlur={(e) =>
              dispatch({ type: 'SET_FILTER', axis: f.axis as keyof DashboardFilter, value: e.target.value.trim() || undefined })
            }
          />
        </div>
      ))}
    </div>
  )
}
